import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";

import { api, type MarketplaceOut } from "@/lib/api";

// ---------------------------------------------------------------------------
// Tipi e stato vuoto
// ---------------------------------------------------------------------------

export interface PriceChangeFilterState {
  marketplace_id: string;
  change_type: string; // "" | "create" | "update" | "manual" | "failed" | "skipped" | "all"
  success: string; // "" | "true" | "false"
  since: string;
  until: string;
  sku: string;
}

export const EMPTY_PRICE_CHANGE_FILTERS: PriceChangeFilterState = {
  marketplace_id: "",
  change_type: "",
  success: "",
  since: "",
  until: "",
  sku: "",
};

// Opzioni dropdown Tipo in ordine presentazione
const CHANGE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Variazioni (default)" },
  { value: "create", label: "Nuova pubblicazione" },
  { value: "update", label: "Aggiornamento" },
  { value: "manual", label: "Manuale" },
  { value: "failed", label: "Fallito" },
  { value: "skipped", label: "Saltato" },
  { value: "all", label: "Tutti (incl. saltati)" },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PriceChangeFiltersProps {
  state: PriceChangeFilterState;
  onChange: (next: PriceChangeFilterState) => void;
  onReset: () => void;
  onExport: () => void;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function PriceChangeFilters({
  state,
  onChange,
  onReset,
  onExport,
}: PriceChangeFiltersProps): JSX.Element {
  const marketplaces = useQuery<MarketplaceOut[]>({
    queryKey: ["marketplaces"],
    queryFn: api.marketplaces,
  });

  const update = <K extends keyof PriceChangeFilterState>(
    key: K,
    value: PriceChangeFilterState[K],
  ): void => onChange({ ...state, [key]: value });

  return (
    <div className="card-stack space-y-3 bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* SKU */}
        <div className="control-shell flex h-10 flex-1 min-w-[220px] items-center gap-2 px-3">
          <Search className="ic text-ink-3" strokeWidth={1.6} aria-hidden="true" />
          <input
            value={state.sku}
            onChange={(e) => update("sku", e.target.value)}
            placeholder="Cerca SKU…"
            className="w-full bg-transparent outline-none"
            aria-label="Cerca SKU"
          />
        </div>

        {/* Canale (marketplace) */}
        <select
          value={state.marketplace_id}
          onChange={(e) => update("marketplace_id", e.target.value)}
          className="control-shell h-10 px-3"
          aria-label="Filtra per canale"
        >
          <option value="">Tutti i canali</option>
          {(marketplaces.data ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>

        {/* Tipo */}
        <select
          value={state.change_type}
          onChange={(e) => update("change_type", e.target.value)}
          className="control-shell h-10 px-3"
          aria-label="Filtra per tipo"
        >
          {CHANGE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Esito */}
        <select
          value={state.success}
          onChange={(e) => update("success", e.target.value)}
          className="control-shell h-10 px-3"
          aria-label="Filtra per esito"
        >
          <option value="">Tutti</option>
          <option value="true">OK</option>
          <option value="false">KO</option>
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Da */}
        <label className="control-shell flex h-10 items-center gap-2 px-3 text-xs text-ink-3">
          Da
          <input
            type="datetime-local"
            value={state.since}
            onChange={(e) => update("since", e.target.value)}
            className="bg-transparent text-sm outline-none"
            aria-label="Data inizio"
          />
        </label>

        {/* A */}
        <label className="control-shell flex h-10 items-center gap-2 px-3 text-xs text-ink-3">
          A
          <input
            type="datetime-local"
            value={state.until}
            onChange={(e) => update("until", e.target.value)}
            className="bg-transparent text-sm outline-none"
            aria-label="Data fine"
          />
        </label>

        <button type="button" className="btn btn-ghost btn-sm" onClick={onReset}>
          <X className="ic" strokeWidth={1.6} aria-hidden="true" /> Reset
        </button>
        <span className="flex-1" />
        <button type="button" className="btn btn-outline btn-sm" onClick={onExport}>
          Export CSV
        </button>
      </div>
    </div>
  );
}
